import { ThreeDModels } from 'src/app/core/constants/3D/3d-models';
import { FloatingDefaultProps } from 'src/app/core/constants/floating-default-props';
import { FLOATING_TYPES } from 'src/app/core/constants/floating-types';
import { Simulation } from 'src/app/core/models/simulation.model';
import { BufferGeometry, Group, MeshBasicMaterial } from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Catenary3D } from '../../catenary-answer';
import { Floating } from '../../floating.model';
import ObjectBuilder from '../object-builders/object-builder.model';

export class FloatingBuilder extends ObjectBuilder {
  private _loader: GLTFLoader;

  constructor() {
    super();
  }

  get loader(): GLTFLoader {
    this._loader = this._loader || new GLTFLoader();
    return this._loader;
  }

  private get floating(): Group {
    return this._object;
  }

  private set floating(floating) {
    this._object = floating;
  }

  async build(_riser: Catenary3D, simulation: Simulation, floating: Floating): Promise<Group> {
    const verticalProjection = simulation.global?.length > 0 ? simulation.global[0].seabedDepth : 0;
    const modelUrl = floating.model === FLOATING_TYPES.SHIP ? ThreeDModels.SHIP : ThreeDModels.PLAT;
    const gltf = await this.load(modelUrl);
    this.floating = gltf.scene;
    this.transform(floating);
    this.setProps(floating, verticalProjection);
    this.floating.name = `[${floating.title}].${floating.internalId}`;
    return this.floating;
  }

  load(modelUrl: string): Promise<GLTF> {
    return new Promise((resolve) => {
      return this.loader.load(modelUrl, resolve);
    });
  }

  setProps(floating: Floating, verticalProjection: number): void {
    const x = floating?.origin?.x;
    const y = floating?.origin?.y;
    const draft = floating?.draft;
    const degrees = floating?.azimuth;
    const z = verticalProjection - draft;
    const azimuthRad = (degrees * Math.PI) / 180;
    const rotationRad = Math.PI / 2 - azimuthRad;
    this.floating.name = `${floating.title}-${floating.internalId}`;
    this.floating.position.set(x, y, z);
    this.floating.rotation.set(0, 0, rotationRad);
  }

  createGeometry(_catenary3D: Catenary3D, _floating: Floating): BufferGeometry {
    return null;
  }

  createMaterial(_catenary3D: Catenary3D, floating: Floating): MeshBasicMaterial {
    return new MeshBasicMaterial({ color: floating.graphicProperties.color });
  }

  transformShip(floating: Floating): void {
    const [length, beam, height] = floating.dimensions;
    const {
      LENGTH: defaultLength,
      BEAM: defaultBeam,
      UPPER_SHIP: upperShip,
      DOWN_SHIP: downShip,
    } = FloatingDefaultProps.SHIP;
    const defaultHeight = downShip + upperShip;
    const x = length / defaultLength;
    const y = beam / defaultBeam;
    const z = height / (downShip / defaultHeight) / defaultHeight;
    this.floating.scale.set(x, y, z);
  }

  transformPlatform(floating: Floating): void {
    const [length, beam, height] = floating.dimensions;
    const {
      LENGTH: defaultLength,
      BEAM: defaultBeam,
      HEIGHT: defaultHeight,
    } = FloatingDefaultProps.PLAT;
    const x = length / defaultLength;
    const y = beam / defaultBeam;
    const z = height / defaultHeight;
    this.floating.scale.set(x, y, z);
  }

  transform(floating: Floating): void {
    if (floating.model === FLOATING_TYPES.SHIP) {
      this.transformShip(floating);
    }
    if (floating.model === FLOATING_TYPES.PLATFORM) {
      this.transformPlatform(floating);
    }
  }

  focus(): void {}
}
